import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Input,
  OnInit,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import {
  DistributionStrategy,
  DistributionStrategyType,
  RolloutPlanStore
} from '../../../+state/rollout/rollout-plan-form.store';
import { useRolloutPlanStore } from '../../../+state/rollout/rollout-plan.store';

import {
  getAvailablePercentages,
  isReadonlyWhitelistStatus,
  parseDeviceList
} from './distribution-settings.helpers';
import { DistributionSettingsService } from './distribution-settings.service';

type PercentageSelection = Record<number, number | undefined>;

@Component({
  selector: 'remote-update-domain-app-distribution-settings',
  templateUrl: './distribution-settings.html',
  styleUrls: ['./distribution-settings.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    CommonModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatInput,
    CdkTextareaAutosize,
    ReactiveFormsModule
  ]
})
export class DistributionSettings implements OnInit {
  @Input() currentSection = '';

  private readonly rolloutPlanGlobalStore = inject(RolloutPlanStore);
  private readonly distributionSettingsService = inject(DistributionSettingsService);
  private readonly store = inject(useRolloutPlanStore);

  readonly DistributionStrategyType = DistributionStrategyType;

  readonly rolloutPercentages = [0, 1, 5, 10, 25, 50, 100];

  private initialized = false;

  distributionStrategy = signal<DistributionStrategyType>(DistributionStrategyType.Percentage);

  finalized = signal(false);
  showError = signal(false);

  selectedPercentages = signal<PercentageSelection>({});
  initialPercentages = signal<PercentageSelection>({});

  initialDevices = signal<string[]>([]);
  private lastValidatedDevices = signal<string[]>([]);

  deviceListControl = new FormControl('', {
    nonNullable: true
  });

  deviceList = toSignal(this.deviceListControl.valueChanges, {
    initialValue: this.deviceListControl.value
  });

  isEditingRolloutPlan = computed(() => this.rolloutPlanGlobalStore.isEditingRolloutPlan());

  groupProducts = computed(() =>
    this.rolloutPlanGlobalStore.productGroups().productGroups.map(productGroup => ({
      id: productGroup.productGroupId,
      name: productGroup.name
    }))
  );

  isWhitelistStrategy = computed(
    () => this.distributionStrategy() === DistributionStrategyType.Whitelist
  );

  isPercentageStrategy = computed(
    () => this.distributionStrategy() === DistributionStrategyType.Percentage
  );

  deviceListValidationResult = computed(() => {
    const data = this.store.validateDeviceWhitelist().data;
    return data?.errors?.length ? data.errors : null;
  });

  constructor() {
    this.validateRolloutPercentages();
    this.setupEffects();
  }

  async ngOnInit(): Promise<void> {
    await this.initializeProductGroups();

    if (this.isEditingRolloutPlan()) {
      this.initializeEditMode();
    } else {
      this.validateRolloutPercentages();
    }

    this.initialized = true;
  }

  // =========================================================
  // Effects
  // =========================================================

  private setupEffects(): void {
    this.setupStrategyEffect();
    this.setupWhitelistEffect();
    this.setupValidationEffect();
    this.setupProductGroupDefaultsEffect();
  }

  private setupStrategyEffect(): void {
    effect(() => {
      const strategy = this.distributionStrategy();

      if (!this.initialized) return;

      this.showError.set(false);

      if (strategy !== DistributionStrategyType.Percentage) return;
      this.deviceListControl.setValue('', {
        emitEvent: false
      });

      this.validateRolloutPercentages();
    });
  }

  private setupWhitelistEffect(): void {
    effect(() => {
      const strategy = this.distributionStrategy();
      const deviceList = this.deviceList();

      if (!this.initialized) return;

      if (strategy !== DistributionStrategyType.Whitelist) {
        return;
      }

      if (deviceList.trim().length > 0) {
        this.showError.set(false);
      }

      this.triggerDeviceWhitelistValidation(deviceList);
    });
  }

  private setupProductGroupDefaultsEffect(): void {
    effect(() => {
      const groups = this.groupProducts();
      if (groups.length === 0) return;

      this.selectedPercentages.update(current => {
        const updated = { ...current };
        let changed = false;
        groups.forEach(group => {
          if (updated[group.id] === undefined) {
            updated[group.id] = 0;
            changed = true;
          }
        });
        return changed ? updated : current;
      });
    });
  }

  private setupValidationEffect(): void {
    effect(() => {
      if (!this.isWhitelistStrategy()) return;

      const devices = this.lastValidatedDevices();

      if (devices.length === 0) return;

      const validationErrors = this.deviceListValidationResult();

      this.updateWhitelistState(devices, validationErrors === null, validationErrors ?? '');
    });
  }

  // =========================================================
  // Initialization
  // =========================================================

  private async initializeProductGroups(): Promise<void> {
    const existing = this.selectedPercentages();
    const selections: PercentageSelection = {};

    this.groupProducts().forEach(group => {
      selections[group.id] = existing[group.id] ?? 0;
    });

    this.selectedPercentages.set(selections);
  }

  private initializeEditMode(): void {
    const strategy = this.rolloutPlanGlobalStore.distributionSettings().strategy;

    switch (strategy.strategyType) {
      case DistributionStrategyType.Whitelist:
        this.initializeWhitelistStrategy(strategy);
        break;

      case DistributionStrategyType.Percentage:
        this.initializePercentageStrategy(strategy);
        break;
    }
  }

  private initializeWhitelistStrategy(
    strategy: Extract<DistributionStrategy, { strategyType: DistributionStrategyType.Whitelist }>
  ): void {
    this.distributionStrategy.set(DistributionStrategyType.Whitelist);

    const devices = strategy.btFriendlyNames ?? [];

    const status = this.rolloutPlanGlobalStore.rolloutPlanStatusInEdit();

    if (this.isReadonlyWhitelistStatus(status)) {
      this.initializeReadonlyWhitelist(devices);
      return;
    }

    this.initializeEditableWhitelist(devices);
  }

  private initializeReadonlyWhitelist(devices: string[]): void {
    this.initialDevices.set(devices);

    this.deviceListControl.setValue('', {
      emitEvent: false
    });

    this.lastValidatedDevices.set(devices);

    if (devices.length > 0) {
      this.updateWhitelistState(devices, true);
    }
  }

  private initializeEditableWhitelist(devices: string[]): void {
    this.initialDevices.set([]);

    this.deviceListControl.setValue(devices.join(';'));

    this.lastValidatedDevices.set(devices);

    if (devices.length > 0) {
      this.updateWhitelistState(devices, true);
    }
  }

  private initializePercentageStrategy(
    strategy: Extract<DistributionStrategy, { strategyType: DistributionStrategyType.Percentage }>
  ): void {
    this.distributionStrategy.set(DistributionStrategyType.Percentage);

    const selections: PercentageSelection = {};

    strategy.productGroupPercentages.forEach(
      (group: { productGroupId: number; percentage: number }) => {
        selections[group.productGroupId] = group.percentage;
      }
    );

    this.selectedPercentages.set(selections);

    this.initialPercentages.set({
      ...selections
    });

    this.validateRolloutPercentages();
  }

  // =========================================================
  // Handlers
  // =========================================================

  onPercentageChange(value: number, groupId: number): void {
    this.selectedPercentages.update(current => ({
      ...current,
      [groupId]: value
    }));

    this.validateRolloutPercentages();
  }

  markAttempted(): void {
    this.showError.set(true);
  }

  // =========================================================
  // Validation
  // =========================================================

  validateRolloutPercentages(): void {
    const percentages = this.getSelectedGroupProductsAndPercentages();

    this.updatePercentageState(
      percentages.map(group => ({
        productGroupId: group.productGroupId,
        percentage: group.rollout_percentage
      }))
    );
  }

  validateDeviceWhiteList(deviceListValue?: string): void {
    this.triggerDeviceWhitelistValidation(deviceListValue ?? this.deviceList());
  }

  private triggerDeviceWhitelistValidation(deviceListValue: string): void {
    const newDevices = parseDeviceList(deviceListValue);

    const initialDevices = this.initialDevices();

    const devices =
      initialDevices.length > 0 ? [...new Set([...initialDevices, ...newDevices])] : newDevices;

    if (devices.length === 0 || (initialDevices.length === 0 && newDevices.length === 0)) {
      this.lastValidatedDevices.set([]);

      this.updateWhitelistState(
        [],
        false,
        'Please add at least one Bt-friendly name to the device list.'
      );

      return;
    }

    const targetProductGroupIds = this.getSelectedGroupProductsAndPercentages().map(
      group => group.productGroupId
    );

    if (targetProductGroupIds.length === 0) {
      if (devices.length > 0) {
        this.updateWhitelistState(devices, true);
      }

      return;
    }

    this.lastValidatedDevices.set(devices);

    this.distributionSettingsService.loadValidationsForDeviceWhiteList({
      targetProductGroupIds,
      btFriendlyNames: devices
    });
  }

  // =========================================================
  // Store Updates
  // =========================================================

  private updateWhitelistState(
    devices: string[],
    isValid: boolean,
    error: string | string[] = ''
  ): void {
    this.rolloutPlanGlobalStore.updateDistributionSettings(
      {
        strategy: {
          strategyType: DistributionStrategyType.Whitelist,
          btFriendlyNames: devices
        },
        isValid
      },
      error
    );
  }

  private updatePercentageState(
    percentages: {
      productGroupId: number;
      percentage: number;
    }[]
  ): void {
    this.rolloutPlanGlobalStore.updateDistributionSettings(
      {
        strategy: {
          strategyType: DistributionStrategyType.Percentage,
          productGroupPercentages: percentages
        },
        isValid: true
      },
      ''
    );
  }

  // =========================================================
  // Utilities
  // =========================================================

  getSelectedGroupProductsAndPercentages() {
    const selections = this.selectedPercentages();

    return this.groupProducts().map(group => ({
      productGroupId: group.id,
      target_product_group: group.name,
      rollout_percentage: selections[group.id] ?? 0
    }));
  }

  getAvailablePercentages(groupId: number): number[] {
    return getAvailablePercentages(
      groupId,
      this.rolloutPercentages,
      this.isEditingRolloutPlan(),
      this.rolloutPlanGlobalStore.rolloutPlanStatusInEdit(),
      this.initialPercentages()
    );
  }

  private isReadonlyWhitelistStatus(status: string): boolean {
    return isReadonlyWhitelistStatus(status);
  }

  // =========================================================
  // Errors
  // =========================================================

  get error(): string | string[] | undefined {
    return this.rolloutPlanGlobalStore.errors().distributionSettings;
  }

  get errorMessages(): string[] | null {
    const err = this.error;

    return Array.isArray(err) && err.length > 0 ? err : null;
  }

  get errorMessage(): string | null {
    const err = this.error;

    return typeof err === 'string' && err.length > 0 ? err : null;
  }
}
